import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash, faEye, faInfoCircle, faLock } from "@fortawesome/free-solid-svg-icons";
import { notification } from "antd";
import { login, verifyLoginOtpThirdParty } from "../../../controllers/accounts/Account";
import "../winningVouchers.css";

const LoginPopup = ({ open, onClose, onLoginSuccess }) => {
  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
    otp: "",
  });
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [loginSuccess, setLoginSuccess] = useState("");
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isCounting, setIsCounting] = useState(false);

  // OTP Countdown effect
  useEffect(() => {
    let countdownInterval;
    if (isCounting) {
      countdownInterval = setInterval(() => {
        if (countdown > 0) {
          setCountdown((prevCountdown) => prevCountdown - 1);
        } else {
          clearInterval(countdownInterval);
          setIsCounting(false);
        }
      }, 1000);
    }
    return () => {
      clearInterval(countdownInterval);
    };
  }, [countdown, isCounting]);

  // Reset form when popup closes
  useEffect(() => {
    if (!open) {
      setLoginFormData({ email: "", password: "", otp: "" });
      setIsOTPSent(false);
      setLoginErrors({});
      setLoginSuccess("");
      setShowPassword(false);
      setCountdown(60);
      setIsCounting(false);
    }
  }, [open]);

  // Handle login form change
  const handleLoginChange = (e) => {
    setLoginFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setLoginErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
      formError: "",
    }));
  };

  // Handle login (send OTP)
  const handleLogin = async () => {
    setIsLoginLoading(true);
    try {
      if (!loginFormData?.email) {
        setLoginErrors({ email: "Email is required." });
        setIsLoginLoading(false);
        return;
      }
      if (!loginFormData.password) {
        setLoginErrors({ password: "Password is required." });
        setIsLoginLoading(false);
        return;
      }

      const options = { email: loginFormData?.email, password: loginFormData.password };
      const result = await login(options);

      if (result.status === true) {
        setIsOTPSent(true);
        setLoginSuccess(result.message);
        setCountdown(60);
        setIsCounting(true);
        setLoginErrors({});
      } else {
        setIsOTPSent(false);
        setLoginErrors({ formError: result.message });
      }
    } catch (error) {
      setLoginErrors({ formError: "Something went wrong. Please try again." });
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async () => {
    setIsLoginLoading(true);
    try {
      if (!loginFormData?.email) {
        setLoginErrors({ email: "Email is required." });
        setIsLoginLoading(false);
        return;
      }
      if (!loginFormData.otp) {
        setLoginErrors({ otp: "One Time Password is required." });
        setIsLoginLoading(false);
        return;
      }

      const options = {
        email: loginFormData?.email,
        otp: loginFormData.otp,
      };
      const result = await verifyLoginOtpThirdParty(options);

      if (result.status === true) {
        notification.open({
          message: "Success",
          description: result.message,
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "green" }} icon={faInfoCircle} />,
          duration: 2,
        });
        // Reset form
        setLoginFormData({ email: "", password: "", otp: "" });
        setIsOTPSent(false);
        setIsCounting(false);
        setCountdown(60);
        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        setLoginErrors({ formError: result.message });
      }
    } catch (error) {
      setLoginErrors({ formError: "Something went wrong. Please try again." });
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Handle email change (reset OTP)
  const handleEmailChange = () => {
    setLoginFormData((prev) => ({
      ...prev,
      password: "",
      otp: "",
    }));
    setIsOTPSent(false);
    setCountdown(0);
    setIsCounting(false);
  };

  // Resend OTP
  const handleResendOTP = async () => {
    await handleLogin();
  };

  //Submit when enter key is pressed
  const handleLoginKeyUp = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  //Submit when enter key is pressed
  const handleVerifyOTPKeyUp = (e) => {
    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "popup-container",
        style: {
          borderRadius: 20,
          padding: 0,
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          padding: "32px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 3,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
            }}
          >
            <FontAwesomeIcon icon={faLock} style={{ fontSize: "28px" }} />
          </Box>
        </Box>

        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            marginBottom: 1,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Authentication Required
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            textAlign: "center",
            marginBottom: 3,
          }}
        >
          Please login to access your assigned winning vouchers
        </Typography>

        {/* Login Form */}
        {!isOTPSent ? (
          <>
            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-email" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> Email Address
              </label>
              <input
                type="email"
                id="login-email"
                name="email"
                className={`winning-vouchers-form-input ${loginErrors?.email ? "error" : ""}`}
                value={loginFormData?.email}
                onChange={handleLoginChange}
                placeholder="Enter your email"
              />
              {loginErrors?.email && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.email}
                </p>
              )}
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-password" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> Password
              </label>
              <div className="" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="login-password"
                  name="password"
                  className={`winning-vouchers-form-input ${loginErrors?.password ? "error" : ""}`}
                  value={loginFormData?.password}
                  onChange={handleLoginChange}
                  onKeyUp={handleLoginKeyUp}
                  style={{
                    borderRadius: "8px 0 0 8px",
                  }}
                  placeholder="Enter your password"
                />
                <span
                  className="input-group-text"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    border: loginErrors?.password ? "2px solid #ef4444" : "1px solid #d1d5db",
                    borderLeft: "none",
                    borderRadius: "0 8px 8px 0",
                    color: "#6b7280",
                    transition: "all 0.2s ease",
                  }}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </span>
              </div>
              {loginErrors?.password && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.password}
                </p>
              )}
            </Box>

            {loginErrors?.formError && (
              <Box className="winning-vouchers-form-error-box" sx={{ marginBottom: 2 }}>
                <p>{loginErrors.formError}</p>
              </Box>
            )}

            <button
              type="button"
              onClick={handleLogin}
              disabled={isLoginLoading}
              className="btn btn-dark"
              style={{
                width: "100%",
                height: "44px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 600,
                marginTop: "8px",
              }}
            >
              {isLoginLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  <span>Processing...</span>
                </Box>
              ) : (
                "Send OTP"
              )}
            </button>
          </>
        ) : (
          <>
            {loginSuccess && (
              <Box className="winning-vouchers-form-success" sx={{ marginBottom: 2 }}>
                <p>
                  {loginSuccess}{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEmailChange();
                    }}
                    style={{ color: "#059669", fontWeight: 600 }}
                  >
                    Change
                  </a>
                </p>
              </Box>
            )}

            <Box sx={{ marginBottom: 2 }}>
              <label htmlFor="login-otp" className="winning-vouchers-form-label">
                <span style={{ color: "red" }}>*</span> One Time Password (OTP)
              </label>
              <input
                type="text"
                id="login-otp"
                name="otp"
                className={`winning-vouchers-form-input ${loginErrors?.otp ? "error" : ""}`}
                value={loginFormData?.otp}
                onChange={handleLoginChange}
                onKeyUp={handleVerifyOTPKeyUp}
                style={{
                  textAlign: "center",
                  letterSpacing: "4px",
                }}
                placeholder="Enter OTP"
                maxLength={6}
              />
              {loginErrors?.otp && (
                <p className="winning-vouchers-form-error">
                  {loginErrors.otp}
                </p>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <span style={{ color: "#6b7280", fontSize: "14px" }}>
                {isCounting ? `Resend OTP in ${countdown}s` : ""}
              </span>
              {!isCounting && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleResendOTP();
                  }}
                  style={{ color: "#667eea", fontSize: "14px", fontWeight: 600 }}
                >
                  Resend OTP
                </a>
              )}
            </Box>

            {loginErrors?.formError && (
              <Box className="winning-vouchers-form-error-box" sx={{ marginBottom: 2 }}>
                <p>{loginErrors.formError}</p>
              </Box>
            )}

            <button
              type="button"
              onClick={handleVerifyOTP}
              disabled={isLoginLoading}
              className="btn btn-dark"
              style={{
                width: "100%",
                height: "44px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 600,
                marginTop: "8px",
              }}
            >
              {isLoginLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  <span>Verifying...</span>
                </Box>
              ) : (
                "Verify & Login"
              )}
            </button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default LoginPopup;
